import { call, takeEvery, put } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import { SuperAdminActions } from 'actions';
import { sendAction } from '../api';
import { transformUser } from '../shared';
import { verifyUsersEmailsSuccess } from '../actions';
import { SUPER_ADMIN_VERIFY_USERS_EMAILS } from '../types';

// Minimal shape of the superadmin API config passed through to `sendAction`.
interface SuperClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  timeout?: number;
  db: {
    users: {
      update: (id: string, user: unknown) => unknown;
    };
  };
}

// Minimal shape of the user we operate on.
interface SuperClientUser {
  primaryEmail: string;
}

// Wrapped response returned by `sendAction`. `data` is a full user record
// (carrying at least `id` and `primaryEmail`) that gets passed to `transformUser`.
interface VerifyUserResponse {
  succeeded: boolean;
  data: SuperClientUser & { id: string; [key: string]: unknown };
}

// Result returned from a single verify attempt (either the transformed response
// or a failure fallback carrying the original user).
type VerifyResult =
  | { succeeded: boolean; data: unknown }
  | { succeeded: false; data: SuperClientUser };

// Action dispatched to trigger the verify-users-emails saga.
interface VerifyUsersEmailsAction {
  type: string;
  users: SuperClientUser[];
}

function* verifyUserEmail(
  config: SuperClientConfig,
  user: SuperClientUser,
): Generator<unknown, VerifyResult, unknown> {
  try {
    const action = CloudActions.adminSetEmailVerificationState(
      user.primaryEmail,
      true,
    );
    const res = (yield call(sendAction, config, action)) as VerifyUserResponse;
    if (res.succeeded) {
      yield config.db.users.update(res.data.id, transformUser(res.data));
    }
    return { ...res, data: transformUser(res.data) };
  } catch (error) {
    return { succeeded: false, data: user };
  }
}

export function* verifyUsersEmails(
  config: SuperClientConfig,
  action: VerifyUsersEmailsAction,
): Generator<unknown, void, unknown> {
  try {
    const { users } = action;
    const responses: VerifyResult[] = [];
    for (let i = 0; i < users.length; i++) {
      const res = (yield verifyUserEmail(config, users[i])) as VerifyResult;
      responses.push(res);
    }
    yield put(verifyUsersEmailsSuccess(responses));
    yield put(SuperAdminActions.deselectAllUserRows());
  } catch (error) {
    console.error('verifyUsersEmails failed -- ', error);
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchVerifyUsersEmails(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(SUPER_ADMIN_VERIFY_USERS_EMAILS, verifyUsersEmails, config);
}
